/**
 * P0.8 - NO RAW RESPONSE ENFORCEMENT
 * 
 * This is the ONLY place fetch() is called.
 * This is the ONLY place that receives a Response object.
 * 
 * Contract:
 * - Calls NATIVE fetch (bypasses any interceptors)
 * - Checks bodyUsed (trap)
 * - Calls safeReadResponse() 
 * - Returns parsed result { ok, status, traceId, json, rawText, error }
 * - NEVER returns a Response object
 * - NEVER lets callers see a Response object
 * 
 * 520 Hardening (Deliverable C):
 * - Classifies 5xx and network errors as BACKEND_UNAVAILABLE
 * - Supports exponential backoff retry (1s, 2s, 4s)
 * - Returns is_backend_unavailable flag for UI handling
 * 
 * CRITICAL: Captures native fetch at module load to bypass Emergent/rrweb interceptors
 */

import { safeReadResponse } from './response';

// ============================================================================
// NATIVE FETCH CAPTURE - Must happen at module load, before any interceptors
// ============================================================================
// This captures the real fetch before emergent-main.js or rrweb can wrap it.
// If this module loads after the wrapper, we still try to use it, but log a warning.
const nativeFetch = (function captureNativeFetch() {
  // Check if fetch looks wrapped (common wrapper pattern adds properties)
  const f = window.fetch;
  if (f && f.toString && !f.toString().includes('[native code]')) {
    console.warn('[apiFetch] WARNING: fetch may already be wrapped. Body stream issues possible.');
  }
  return f ? f.bind(window) : null;
})();

// Error classification
const BACKEND_UNAVAILABLE_CODES = [500, 502, 503, 504, 520, 521, 522, 523, 524];

/**
 * Classify an error as BACKEND_UNAVAILABLE or not.
 */
function classifyError(status, error) {
  // Network errors
  if (status === 0 || error?.code === 'NETWORK_ERROR') {
    return {
      is_backend_unavailable: true,
      classification: 'NETWORK_FAILURE',
      can_retry: true
    };
  }
  
  // Gateway/server errors (including 520)
  if (BACKEND_UNAVAILABLE_CODES.includes(status)) {
    return {
      is_backend_unavailable: true,
      classification: status === 520 ? 'GATEWAY_520' : `SERVER_${status}`,
      can_retry: true
    };
  }
  
  // Empty response on error (indicates proxy failure)
  if (!error && status >= 400) {
    return {
      is_backend_unavailable: status >= 500,
      classification: 'EMPTY_RESPONSE',
      can_retry: status >= 500
    };
  }
  
  return {
    is_backend_unavailable: false,
    classification: 'APPLICATION_ERROR',
    can_retry: false
  };
}

/**
 * Delay helper for retry backoff.
 */
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Centralized fetch wrapper - the ONLY fetch entry point.
 * Uses NATIVE fetch to bypass any interceptors (emergent-main.js, rrweb, etc.)
 * 
 * @param {string} url - URL to fetch
 * @param {RequestInit} options - Fetch options
 * @param {Object} retryConfig - Optional retry configuration
 * @returns {Promise<{ok, status, traceId, json, rawText, error, is_backend_unavailable}>}
 */
export async function apiFetch(url, options = {}, retryConfig = { maxRetries: 0, backoff: [1000, 2000, 4000] }) {
  const { maxRetries = 0, backoff = [1000, 2000, 4000] } = retryConfig;
  
  // Use native fetch if available, fallback to window.fetch
  const fetchFn = nativeFetch || window.fetch.bind(window);
  
  let lastResult = null;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    let res;
    try {
      res = await fetchFn(url, options);
    } catch (networkErr) {
      console.error(`[apiFetch] P0.8: Network error (attempt ${attempt + 1}):`, networkErr.message);
      lastResult = {
        ok: false,
        status: 0,
        traceId: null,
        json: null,
        rawText: null,
        error: {
          code: 'NETWORK_ERROR',
          message: `Network request failed: ${networkErr.message}`
        },
        ...classifyError(0, { code: 'NETWORK_ERROR' })
      };
      
      // Retry on network error
      if (attempt < maxRetries) {
        const waitTime = backoff[attempt] || backoff[backoff.length - 1];
        console.log(`[apiFetch] Retrying in ${waitTime}ms...`);
        await delay(waitTime);
        continue;
      }
      return lastResult;
    }
    
    // P0.8 HARD GUARD: If bodyUsed is true here, something consumed it
    // between fetch() returning and this line - which should be IMPOSSIBLE
    if (res.bodyUsed) {
      console.error('[apiFetch] P0.8 CRITICAL: bodyUsed=true immediately after fetch()', {
        url,
        status: res.status,
        bodyUsed: res.bodyUsed
      });
      return {
        ok: false,
        status: res.status || 0,
        traceId: res.headers?.get('x-trace-id') || null,
        json: null,
        rawText: null,
        error: {
          code: 'BODY_CONSUMED_BEFORE_READ',
          message: 'Response body was consumed before apiFetch could read it. Check for fetch interceptors.'
        },
        is_backend_unavailable: false,
        classification: 'CLIENT_ERROR',
        can_retry: false
      };
    }
    
    // Body is fresh - read it now via safeReadResponse
    const result = await safeReadResponse(res);
    
    // Add error classification
    const classification = classifyError(result.status, result.error);
    lastResult = { ...result, ...classification };
    
    // Retry on backend unavailable
    if (classification.is_backend_unavailable && attempt < maxRetries) {
      const waitTime = backoff[attempt] || backoff[backoff.length - 1];
      console.log(`[apiFetch] Backend unavailable (${result.status}), retrying in ${waitTime}ms...`);
      await delay(waitTime);
      continue;
    }
    
    return lastResult;
  }
  
  return lastResult;
}

/**
 * Authenticated GET wrapper.
 */
export async function apiFetchAuth(url, options = {}) {
  const token = localStorage.getItem('accessToken');
  const headers = {
    ...options.headers,
    'Authorization': `Bearer ${token}`
  };
  return apiFetch(url, { ...options, headers });
}

/**
 * Authenticated JSON POST wrapper.
 */
export async function apiFetchAuthPost(url, body, options = {}) {
  const token = localStorage.getItem('accessToken');
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
    ...options.headers
  };
  return apiFetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
    ...options
  });
}
